import React, { useState, useEffect } from "react";
import { MessageCircle, Send, Mail, X, Sparkles, EyeOff } from "lucide-react";

/**
 * <FounderChatPill /> — fixed bottom-right pill on every HustlePlate
 * page. Restaurant owners open it during service and get a direct line
 * to the founder without booking a formal demo slot. Two channels:
 *
 *   • Telegram — deep-links to `t.me/<REACT_APP_HUSTLEPLATE_TELEGRAM>`
 *   • Email    — mailto:<REACT_APP_HUSTLEPLATE_EMAIL>
 *
 * Env-driven so ops can swap handles without a redeploy. Defaults are
 * hustleplate handles that should be created (or aliased) once.
 *
 * Long-press or right-click the pill to permanently hide it for
 * 14 days.
 */

const TELEGRAM = (process.env.REACT_APP_HUSTLEPLATE_TELEGRAM || "hustleplate").replace(/^@/, "");
const EMAIL    = process.env.REACT_APP_HUSTLEPLATE_EMAIL    || "founder@hustleplate.au";
const HIDE_KEY = "founder-chat-hidden-at";
const NUDGE_AFTER_MS = 14 * 24 * 60 * 60 * 1000;

export default function FounderChatPill({ testid = "hp-founder-chat" }) {
  const [open, setOpen] = useState(false);
  const [hidden, setHidden] = useState(() => {
    if (typeof window === "undefined") return false;
    const at = parseInt(window.localStorage.getItem(HIDE_KEY) || "0", 10);
    return Date.now() - at < NUDGE_AFTER_MS;
  });

  // Auto-shrink when the user clicks outside the pill
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (!e.target.closest?.(`[data-testid="${testid}"]`)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open, testid]);

  const dismiss = () => {
    try { window.localStorage.setItem(HIDE_KEY, String(Date.now())); } catch (_e) {}
    setHidden(true);
    setOpen(false);
  };

  if (hidden) return null;

  const tgHref    = `https://t.me/${TELEGRAM}?text=${encodeURIComponent("Hi — question about HustlePlate for my venue")}`;
  const mailHref  = `mailto:${EMAIL}?subject=${encodeURIComponent("HustlePlate — quick question")}&body=${encodeURIComponent("Hi — I run\n\nWhat I'd like to know:\n\n")}`;

  return (
    <div
      data-testid={testid}
      style={{
        position: "fixed",
        right: 20,
        bottom: 84,
        zIndex: 55,
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-end",
        gap: 10,
        pointerEvents: "auto",
      }}
    >
      {open && (
        <div
          data-testid={`${testid}-menu`}
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 8,
            padding: 14,
            borderRadius: 18,
            background: "rgba(255,255,255,.98)",
            boxShadow: "0 20px 50px rgba(0,0,0,.28)",
            border: "1px solid rgba(15,23,42,.08)",
            minWidth: 260,
            backdropFilter: "blur(12px)",
            animation: "hp-chat-in .18s ease-out",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 6,
                        fontSize: 12, fontWeight: 700, letterSpacing: ".08em",
                        textTransform: "uppercase", color: "#059669" }}>
            <Sparkles size={13} /> Skip the demo — DM the founder
          </div>
          <div style={{ fontSize: 13, color: "#334155", lineHeight: 1.45 }}>
            Two-minute reply during Sydney trading hours. No pitch deck, no forms.
          </div>

          <a
            href={tgHref}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`${testid}-telegram`}
            style={{
              display: "flex", alignItems: "center", gap: 10,
              padding: "10px 14px", marginTop: 4,
              borderRadius: 999,
              background: "#0088cc", color: "#fff",
              fontWeight: 600, textDecoration: "none",
              boxShadow: "0 6px 14px rgba(0,136,204,.35)",
            }}
          >
            <Send size={16} />
            Message on Telegram
          </a>

          <a
            href={mailHref}
            data-testid={`${testid}-email`}
            style={{
              display: "flex", alignItems: "center", gap: 10,
              padding: "10px 14px",
              borderRadius: 999,
              background: "#0f172a", color: "#fff",
              fontWeight: 600, textDecoration: "none",
              boxShadow: "0 6px 14px rgba(15,23,42,.35)",
            }}
          >
            <Mail size={16} />
            Email {EMAIL}
          </a>
        </div>
      )}

      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        {!open && (
          <button
            type="button"
            onClick={dismiss}
            data-testid={`${testid}-hide`}
            aria-label="Hide founder chat for 14 days"
            title="Hide for 14 days"
            style={{
              display: "inline-flex", alignItems: "center", justifyContent: "center",
              width: 28, height: 28,
              borderRadius: 999,
              border: "1px solid rgba(15,23,42,.15)",
              background: "#fff",
              color: "#475569",
              cursor: "pointer",
              boxShadow: "0 4px 10px rgba(15,23,42,.15)",
              padding: 0,
            }}
          >
            <EyeOff size={14} />
          </button>
        )}
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          data-testid={`${testid}-toggle`}
          aria-label={open ? "Close founder chat" : "Open founder chat"}
          aria-expanded={open}
          style={{
            display: "flex", alignItems: "center", gap: 8,
            padding: open ? "12px 14px" : "12px 20px",
            borderRadius: 999,
            border: 0, cursor: "pointer",
            background: open ? "#0f172a" : "linear-gradient(135deg,#059669,#0d9488)",
            color: "#fff",
            fontWeight: 700, fontSize: 14,
            boxShadow: "0 12px 30px rgba(5,150,105,.45)",
            transition: "transform .15s ease-out, box-shadow .15s",
          }}
          onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-2px)"; }}
          onMouseLeave={(e) => { e.currentTarget.style.transform = ""; }}
        >
          {open ? <X size={18} /> : <MessageCircle size={18} />}
          {!open && <span>Chat with founder</span>}
        </button>
      </div>

      <style>{`
        @keyframes hp-chat-in {
          from { opacity: 0; transform: translateY(6px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @media (max-width: 480px) {
          [data-testid="${testid}"] { right: 12px; bottom: 84px; }
          [data-testid="${testid}"] > div > button:last-child > span { display: none; }
        }
      `}</style>
    </div>
  );
}
